import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import {
    DefaultFormProps,
    DefaultFormRefs,
} from 'views/components/form/utils/form-default-props';
import { makeUtilClasses } from 'views';
import { Controller, useForm } from 'react-hook-form';
import { TextFieldSaurus } from 'views/components/controles/inputs';
import { yupResolver } from '@hookform/resolvers/yup';
import { PessoaTaxaFormModel } from 'model/app/forms/pessoa-taxa/pessoa-taxa-form-model';
import { EnumTipoPessoas } from 'model/enums/enum-tipo-pessoas';
import { useSessaoAtual, useToastSaurus } from 'services/app';
import classNames from 'classnames';
import { SelectSaurus } from 'views/components/controles/selects/select-saurus/select-saurus';
import { CredenciaisListModel } from 'model/api/gestao/credenciamento/credenciais-list-model';
import { KeyValueModel } from 'model';
import { useGetCredenciaisDisponiveis } from 'data/api/gestao/credenciamento/get-credenciais-disponiveis';
import { isEmpty } from 'lodash';
import { formatarCPFCNPJ } from 'utils/cpfcnpj-format';
import { InstituicaoPagamentoTodosMock } from 'data/mocks/instituicao-pagamento-mock';
import { useStyles } from './form-pessoa-taxa-styles';
import { useFormTaxasValidation as useFormPessoaTaxaValidation } from './form-pessoa-taxa-validation';
import { toDateString } from 'utils/to-date';
import { toDecimal } from 'utils/to-decimal';
import { PessoaTaxaModel } from 'model/api/gestao/pessoa-taxa/pessoa-taxa-model';

interface FormPessoaTaxaProps extends DefaultFormProps<PessoaTaxaFormModel> {
    tipoPessoa: EnumTipoPessoas;
    pessoaId: string;
}

export const FormPessoaTaxa = forwardRef<
    DefaultFormRefs<PessoaTaxaFormModel>,
    FormPessoaTaxaProps
>(
    (
        {
            loading,
            onSubmit,
            model,
            tipoPessoa,
            pessoaId,
            ...props
        }: FormPessoaTaxaProps,
        ref,
    ) => {

        const utilClasses = makeUtilClasses();
        const classes = useStyles();
        const { showToast } = useToastSaurus();

        const { getCredenciaisDisponiveis } = useGetCredenciaisDisponiveis();
        const [credenciais, setCredenciais] = useState<CredenciaisListModel[]>([])
        const { FormTaxasValidationYup } = useFormPessoaTaxaValidation();

        const { tipoUsuario } = useSessaoAtual();
        const [repasseRevenda, setRepasseRevenda] = useState<number>(0);

        const {
            handleSubmit,
            reset,
            control,
            getFieldState,
            formState: { errors },
            setValue,
            getValues
        } = useForm<PessoaTaxaFormModel>({
            defaultValues: new PessoaTaxaFormModel(),
            criteriaMode: 'all',
            mode: 'onSubmit',
            resolver: yupResolver(FormTaxasValidationYup)
        });
        console.log(errors)
        const calcularRepasseRevenda = useCallback((taxaCliente: number) => {
            var percRevenda = getValues('taxaCustoRevenda.percentual') || 0;
            setRepasseRevenda(taxaCliente - percRevenda);
        }, [getValues]);

        const getCredenciaisWrapper = useCallback(async () => {
            try {
                const res = await getCredenciaisDisponiveis(1, '&pageSize=0')

                if (res.erro) throw res.erro

                setCredenciais(res.resultado?.data.list || []);
                setValue('credencialId', res.resultado?.data.list?.[0]?.id ?? '')
            } catch (e: any) {
                showToast('error', e.message)
            }
        }, [getCredenciaisDisponiveis, setValue, showToast]);

        useEffect(() => {
            if (!credenciais)
                getCredenciaisWrapper();
        }, [credenciais, getCredenciaisWrapper]);


        const submitEnviar = (values: PessoaTaxaFormModel) => {
            onSubmit(values);
        };

        useImperativeHandle(ref, () => ({
            submitForm: () => {
                handleSubmit(submitEnviar)();
            },
            resetForm: () => {
                reset();
            },
            fillForm: (model: PessoaTaxaFormModel) => {
                if (isEmpty(model.taxaCustoCliente?.id)) {
                    model.taxaCustoCliente = new PessoaTaxaModel();
                    model.taxaCustoCliente!.percentual = model.taxaVendaRevenda?.percentual || 0.88;

                    model.taxaCustoCliente.credencialId = model.credencialId;
                    model.taxaCustoCliente.instituicaoFinanceira = model.instituicaoFinanceira;
                    model.taxaCustoCliente.tipoTransacao = model.tipoTransacao;
                }
                reset({ ...model });
                calcularRepasseRevenda(model.taxaCustoCliente?.percentual || 0);
            },
        }));

        const isSoftwareHouse = tipoUsuario() === EnumTipoPessoas.SoftwareHouse
        const isRevenda = tipoUsuario() === EnumTipoPessoas.Revendedor ||
            tipoUsuario() === EnumTipoPessoas.FuncionarioFinanceiro;

        const isReadOnly = !(tipoUsuario() === EnumTipoPessoas.SoftwareHouse ||
            tipoUsuario() === EnumTipoPessoas.Revendedor);

        const labelSwHouse = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Cliente) {
                return 'Custo Original (%)';
            }
            if (tipoPessoa === EnumTipoPessoas.Revendedor) {
                return 'Custo Original (%)';
            }
            if (tipoPessoa === EnumTipoPessoas.SoftwareHouse) {
                return 'Custo Original (%)';
            }
            return '1';
        }, [tipoPessoa]);

        const labelCustoRevenda = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Cliente) {
                return 'Custo Vigente (%)';
            }
            if (tipoPessoa === EnumTipoPessoas.Revendedor) {
                return 'Custo Vigente (%)';
            }
            return '2';
        }, [tipoPessoa]);
        const labelVendaRevenda = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Cliente) {
                return 'Taxa Sugerida (%)';
            }
            if (tipoPessoa === EnumTipoPessoas.Revendedor) {
                return 'Taxa Sugerida ao Cliente (%)';
            }
            return '3';
        }, [tipoPessoa]);
        const labelRepasseRevenda = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Cliente) {
                return 'Repasse Revenda (%)';
            }

            return '4';
        }, [tipoPessoa]);
        const labelCustoCliente = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Cliente) {
                return 'Taxa Vigente (%)';
            }

            return '';
        }, [tipoPessoa]);

        const exibirVendaSoftwareHouse = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Cliente && isSoftwareHouse) {
                return true;
            }
            if (tipoPessoa === EnumTipoPessoas.Revendedor && isSoftwareHouse) {
                return true;
            }
            if (tipoPessoa === EnumTipoPessoas.SoftwareHouse && isSoftwareHouse) {
                return true;
            }
            return false;
        }, [isSoftwareHouse, tipoPessoa]);
        const exibirVendaRevenda = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Revendedor) {
                return true;
            }
            return false;
        }, [tipoPessoa]);
        const exibirCustoRevenda = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Cliente) {
                return true;
            }
            if (tipoPessoa === EnumTipoPessoas.Revendedor) {
                return true;
            }
            return false;
        }, [tipoPessoa]);
        const exibirRepasseRevenda = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Cliente) {
                return true;
            }
            return false;
        }, [tipoPessoa]);
        const exibirCustoCliente = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Cliente) {
                return true;
            }
            return false;
        }, [tipoPessoa]);

        const bloquearVendaSoftwareHouse = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.SoftwareHouse && isSoftwareHouse) {
                return false;
            }
            return true;
        }, [isSoftwareHouse, tipoPessoa]);

        const bloquearCustoRevenda = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Revendedor && isSoftwareHouse) {
                return false;
            }
            return true;
        }, [isSoftwareHouse, tipoPessoa]);

        const bloquearVendaRevenda = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Revendedor && !isReadOnly) {
                return false;
            }
            return true;
        }, [isReadOnly, tipoPessoa]);

        const bloquearCustoCliente = useMemo(() => {
            if (tipoPessoa === EnumTipoPessoas.Cliente && isRevenda) {
                return false;
            }
            return true;
        }, [isRevenda, tipoPessoa]);

        const qtdCampos = useMemo(() =>
            (exibirVendaSoftwareHouse ? 1 : 0) +
            (exibirCustoRevenda ? 1 : 0) +
            (exibirVendaRevenda ? 1 : 0) +
            (exibirRepasseRevenda ? 1 : 0) +
            (exibirCustoCliente ? 1 : 0)
            , [exibirCustoCliente, exibirCustoRevenda, exibirRepasseRevenda, exibirVendaRevenda, exibirVendaSoftwareHouse]);

        return (
            <>
                <form
                    onSubmit={handleSubmit(submitEnviar)}
                    className={classNames(loading ? utilClasses.controlLoading : '', classes.formsContainer)}
                >
                    {(credenciais.length > 0) &&
                        <Box className={classes.container}>
                            <Controller
                                control={control}
                                name='credencialId'
                                render={({ field }) => (
                                    <SelectSaurus
                                        conteudo={credenciais.map(cred => new KeyValueModel(cred.id, (
                                            `${cred.instituicaoFinanceira} (${InstituicaoPagamentoTodosMock.find(x => x.Key === cred.instituicaoFinanceira)?.Value || '-'}) - CNPJ - ${formatarCPFCNPJ(cred.cpfCnpj)}`
                                        )))}
                                        readOnly={credenciais.length <= 1}
                                        label='Credenciais Disponíveis'
                                        fullWidth
                                        {...field}
                                        error={Boolean(errors.credencialId)}
                                        helperText={Boolean(errors.credencialId?.message) ?
                                            errors.credencialId?.message : undefined}
                                    />
                                )}
                            />
                        </Box>
                    }

                    <Box className={classes.container}>
                        <Grid container spacing={2} alignItems='center' justifyContent='center'>
                            {exibirVendaSoftwareHouse && (
                                <Grid item xs={qtdCampos === 1 ? 12 : qtdCampos === 2 ? 6 : qtdCampos === 3 ? 4 : 3} className={classes.labelContainer}>
                                    <Controller
                                        name={`taxaVendaSoftwareHouse.percentual`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextFieldSaurus
                                                label={labelSwHouse}
                                                tipo="DECIMAL"
                                                readOnly={bloquearVendaSoftwareHouse}
                                                manterMascara
                                                helperText={
                                                    getFieldState(`taxaVendaSoftwareHouse.percentual`).error
                                                        ? getFieldState(`taxaVendaSoftwareHouse.percentual`).error?.message
                                                        : undefined
                                                }
                                                error={Boolean(
                                                    getFieldState(`taxaVendaSoftwareHouse.percentual`).error)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            {exibirCustoRevenda && (
                                <Grid item xs={qtdCampos === 1 ? 12 : qtdCampos === 2 ? 6 : qtdCampos === 3 ? 4 : 3}>
                                    <Controller
                                        name={`taxaCustoRevenda.percentual`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextFieldSaurus
                                                label={labelCustoRevenda}
                                                tipo="DECIMAL"
                                                readOnly={bloquearCustoRevenda}
                                                manterMascara
                                                helperText={
                                                    getFieldState(`taxaCustoRevenda.percentual`).error
                                                        ? getFieldState(`taxaCustoRevenda.percentual`).error?.message
                                                        : undefined
                                                }
                                                error={Boolean(
                                                    getFieldState(`taxaCustoRevenda.percentual`).error)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            {exibirVendaRevenda && (
                                <Grid item xs={qtdCampos === 1 ? 12 : qtdCampos === 2 ? 6 : qtdCampos === 3 ? 4 : 3}>
                                    <Controller
                                        name={`taxaVendaRevenda.percentual`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextFieldSaurus
                                                label={labelVendaRevenda}
                                                tipo="DECIMAL"
                                                readOnly={bloquearVendaRevenda}
                                                manterMascara
                                                helperText={
                                                    getFieldState(`taxaVendaRevenda.percentual`).error
                                                        ? getFieldState(`taxaVendaRevenda.percentual`).error?.message
                                                        : undefined
                                                }
                                                error={Boolean(
                                                    getFieldState(`taxaVendaRevenda.percentual`).error)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            {exibirRepasseRevenda && (
                                <Grid item xs={qtdCampos === 1 ? 12 : qtdCampos === 2 ? 6 : qtdCampos === 3 ? 4 : 3}>
                                    <TextFieldSaurus
                                        label={labelRepasseRevenda}
                                        tipo="DECIMAL"
                                        value={repasseRevenda}
                                        readOnly={bloquearCustoRevenda}
                                        manterMascara
                                        helperText={
                                            getFieldState(`taxaCustoRevenda.percentual`).error
                                                ? getFieldState(`taxaCustoRevenda.percentual`).error?.message
                                                : undefined
                                        }
                                        error={Boolean(
                                            getFieldState(`taxaCustoRevenda.percentual`).error)}
                                    />
                                </Grid>
                            )}
                            {exibirCustoCliente && (
                                <Grid item xs={qtdCampos === 1 ? 12 : qtdCampos === 2 ? 6 : qtdCampos === 3 ? 4 : 3}>
                                    <Controller
                                        name={`taxaCustoCliente.percentual`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextFieldSaurus
                                                label={labelCustoCliente}
                                                tipo="DECIMAL"
                                                readOnly={bloquearCustoCliente}
                                                manterMascara
                                                helperText={
                                                    getFieldState(`taxaCustoCliente.percentual`).error
                                                        ? getFieldState(`taxaCustoCliente.percentual`).error?.message
                                                        : undefined
                                                }
                                                error={Boolean(
                                                    getFieldState(`taxaCustoCliente.percentual`).error)}
                                                {...field}
                                                onChange={(e) => {
                                                    calcularRepasseRevenda(toDecimal(e.target.value, 2));
                                                    field.onChange(e);
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                        </Grid>

                    </Box>
                    {tipoPessoa === EnumTipoPessoas.Cliente && (
                        <Box className={classes.container}>
                            {isEmpty(getValues('taxaCustoCliente.id')) && (
                                <Typography>Situação: Não Credenciado</Typography>
                            )}
                            {!isEmpty(getValues('taxaCustoCliente.id')) && (
                                <Typography>Situação: {!isEmpty(getValues('taxaCustoCliente.s2PayEcDataRegistro')) ? ('Credenciado em ' + toDateString(getValues('taxaCustoCliente.s2PayEcDataRegistro'), 'DD/MM/YYYY HH:mm')) : 'Aceite Pendente'}</Typography>
                            )}
                        </Box>
                    )}
                    <Button style={{ display: 'none' }} type="submit"></Button>
                </form>

            </>
        );
    });